/* eslint-disable max-len */
// Option list for the searchable timezone picker, built from moment-timezone's
// IANA data. Only canonical zones are listed — the legacy aliases moment also
// exposes (Asia/Calcutta, US/Pacific, …) are dropped so the list has no
// duplicates — and every zone is labelled with the country it belongs to.
import moment from 'moment-timezone';

// Zones shared by several countries. moment knows the full set but not which
// country the zone is named after, so the primary one is pinned here; the rest
// stay searchable through `option.otherCountries`.
const PRIMARY_COUNTRY = {
  'Africa/Abidjan': 'CI',
  'Africa/Johannesburg': 'ZA',
  'Africa/Lagos': 'NG',
  'Africa/Maputo': 'MZ',
  'Africa/Nairobi': 'KE',
  'America/Panama': 'PA',
  'America/Phoenix': 'US',
  'America/Puerto_Rico': 'PR',
  'America/Toronto': 'CA',
  'Asia/Bangkok': 'TH',
  'Asia/Dubai': 'AE',
  'Asia/Kuching': 'MY',
  'Asia/Qatar': 'QA',
  'Asia/Riyadh': 'SA',
  'Asia/Singapore': 'SG',
  'Asia/Urumqi': 'CN',
  'Asia/Yangon': 'MM',
  'Europe/Belgrade': 'RS',
  'Europe/Berlin': 'DE',
  'Europe/Brussels': 'BE',
  'Europe/Helsinki': 'FI',
  'Europe/London': 'GB',
  'Europe/Paris': 'FR',
  'Europe/Prague': 'CZ',
  'Europe/Rome': 'IT',
  'Europe/Simferopol': 'UA',
  'Europe/Zurich': 'CH',
  'Indian/Maldives': 'MV',
  'Pacific/Auckland': 'NZ',
  'Pacific/Guadalcanal': 'SB',
  'Pacific/Guam': 'GU',
  'Pacific/Pago_Pago': 'AS',
  'Pacific/Port_Moresby': 'PG',
  'Pacific/Tarawa': 'KI',
};

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch (e) {
    return null;
  }
})();

const countryName = (code) => {
  try {
    return regionNames?.of(code) || code;
  } catch (e) {
    return code;
  }
};

// Abbreviations for both halves of the year, so a zone is findable by its
// standard-time code out of season too ("pst" has to find Los Angeles in
// July, when it is reporting PDT). Numeric abbreviations ("+05") say nothing
// the offset doesn't, so they are dropped.
const abbreviationsFor = (zone) => {
  const year = moment().year();
  return [0, 6].reduce((abbrs, month) => {
    const abbr = moment.tz([year, month, 1], zone).format('z');
    if (/^[+-]/.test(abbr) || abbrs.includes(abbr)) return abbrs;
    return [...abbrs, abbr];
  }, []);
};

const buildOption = (zone, country = '', otherCountries = []) => {
  const now = moment.tz(zone);
  const offset = now.format('Z');
  const city = zone.split('/').pop().replace(/_/g, ' ').toLowerCase();
  const currentAbbr = now.format('z');
  const abbrs = abbreviationsFor(zone);
  // Current season first, so the code the clock is on right now leads.
  const orderedAbbrs = abbrs.includes(currentAbbr)
    ? [currentAbbr, ...abbrs.filter((abbr) => abbr !== currentAbbr)]
    : abbrs;

  return {
    zone,
    country,
    otherCountries,
    city,
    abbrs: orderedAbbrs,
    // "Asia/Karachi — Pakistan" is what both the field and the option title show.
    label: country ? `${zone} — ${country}` : zone,
    // "PDT/PST" — one line, both codes, so a search for either is confirmed.
    abbrLabel: orderedAbbrs.join('/'),
    offsetLabel: `GMT${offset}`,
    // Search matches the *start* of a token, never any old substring: "ist"
    // must find Asia/Kolkata (IST) and not every zone in Pakistan.
    tokens: [
      ...zone.toLowerCase().split(/[_/]/),
      ...[country, ...otherCountries].join(' ').toLowerCase().split(/\s+/),
      ...orderedAbbrs.map((abbr) => abbr.toLowerCase()),
      `gmt${offset}`,
      offset,
      offset.replace(/^[+-]/, ''),
    ].filter(Boolean),
  };
};

const buildOptions = () => {
  const countriesByZone = {};
  moment.tz.countries().forEach((code) => {
    moment.tz.zonesForCountry(code).forEach((zone) => {
      countriesByZone[zone] = [...(countriesByZone[zone] || []), code];
    });
  });

  const options = Object.keys(countriesByZone).map((zone) => {
    const codes = countriesByZone[zone];
    const pinned = PRIMARY_COUNTRY[zone];
    const primary = pinned && codes.includes(pinned) ? pinned : codes[0];
    const others = codes.filter((code) => code !== primary).map(countryName).sort((a, b) => a.localeCompare(b));
    return buildOption(zone, countryName(primary), others);
  });

  options.push(buildOption('UTC'));
  return options.sort((a, b) => a.zone.localeCompare(b.zone));
};

let cachedOptions = null;

export const getTimezoneOptions = () => {
  if (!cachedOptions) cachedOptions = buildOptions();
  return cachedOptions;
};

// Resolves a stored value to an option. Values outside the canonical list
// (legacy aliases, or zones moment has no country for) still resolve, just
// without a country, so an existing selection is never silently dropped.
export const findTimezoneOption = (zone) => {
  if (!zone) return null;
  const known = getTimezoneOptions().find((option) => option.zone === zone);
  if (known) return known;
  if (moment.tz.zone(zone)) return buildOption(zone);
  return { ...buildOption('UTC'), zone, label: zone };
};

// Best match first. An exact short code wins, and the code the zone is on
// *right now* beats an out-of-season one, so "ist" leads with Asia/Kolkata
// rather than Asia/Jerusalem (on IDT today, IST in winter). Then exact city
// or country, then prefixes — so "india" leads with India, not Indiana.
const rankFor = (option, query) => {
  const compact = query.replace(/\s+/g, '');
  const abbrIndex = option.abbrs.findIndex((abbr) => abbr.toLowerCase() === compact);
  if (abbrIndex === 0) return 0;
  if (abbrIndex > 0) return 1;
  const country = option.country.toLowerCase();
  if (option.city === query || country === query) return 2;
  if (option.city.startsWith(query)) return 3;
  if (country.startsWith(query)) return 4;
  return 5;
};

export const filterTimezoneOptions = (query) => {
  // Slashes and underscores are normalised away on both sides, so typing
  // "europe/lond" or "america/new_york" matches like the words they are.
  const normalized = (query || '')
    .toLowerCase()
    .replace(/[_/]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
  if (!normalized) return getTimezoneOptions();

  const terms = normalized.split(' ');
  return getTimezoneOptions()
    .filter((option) => terms.every((term) => option.tokens.some((token) => token.startsWith(term))))
    .sort((a, b) => rankFor(a, normalized) - rankFor(b, normalized) || a.zone.localeCompare(b.zone));
};
